import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import axios from "axios";
import { toast } from "sonner";
import { Send } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { INQUIRY_TYPES } from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const schema = z.object({
  name: z.string().min(2, "Please enter your name").max(120),
  organization: z.string().max(160).optional().or(z.literal("")),
  email: z.string().email("Please enter a valid email address"),
  phone: z.string().max(40).optional().or(z.literal("")),
  inquiry_type: z.string().min(1, "Please select an inquiry type"),
  message: z.string().min(10, "Please tell us a little more (min. 10 characters)").max(3000),
  website: z.string().optional().or(z.literal("")),
});

const fieldClass =
  "bg-white border-slate-300 text-slate-900 placeholder:text-slate-400 focus-visible:ring-emerald-600 h-12";
const errorClass = "mt-1.5 text-xs text-red-600";

export default function ContactForm() {
  const [submitting, setSubmitting] = useState(false);
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm({ resolver: zodResolver(schema), defaultValues: { inquiry_type: "" } });

  const onSubmit = async (values) => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, values);
      toast.success("Inquiry sent", {
        description: "Thank you. The BNM team will respond to your inquiry shortly.",
      });
      reset();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error("Could not send inquiry", {
        description: typeof detail === "string" ? detail : "Please try again, or email us directly.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      data-testid="contact-form"
      onSubmit={handleSubmit(onSubmit)}
      noValidate
      className="space-y-5"
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div>
          <Label htmlFor="contact-name" className="text-slate-700">Name *</Label>
          <Input id="contact-name" data-testid="contact-name-input" autoComplete="name" className={fieldClass} {...register("name")} />
          {errors.name && <p role="alert" className={errorClass}>{errors.name.message}</p>}
        </div>
        <div>
          <Label htmlFor="contact-organization" className="text-slate-700">Organization</Label>
          <Input id="contact-organization" data-testid="contact-organization-input" autoComplete="organization" className={fieldClass} {...register("organization")} />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div>
          <Label htmlFor="contact-email" className="text-slate-700">Email *</Label>
          <Input id="contact-email" type="email" data-testid="contact-email-input" autoComplete="email" className={fieldClass} {...register("email")} />
          {errors.email && <p role="alert" className={errorClass}>{errors.email.message}</p>}
        </div>
        <div>
          <Label htmlFor="contact-phone" className="text-slate-700">Phone</Label>
          <Input id="contact-phone" type="tel" data-testid="contact-phone-input" autoComplete="tel" className={fieldClass} {...register("phone")} />
        </div>
      </div>

      <div>
        <Label htmlFor="contact-inquiry-type" className="text-slate-700">Inquiry Type *</Label>
        <Select value={watch("inquiry_type")} onValueChange={(v) => setValue("inquiry_type", v, { shouldValidate: true })}>
          <SelectTrigger id="contact-inquiry-type" data-testid="contact-inquiry-type-select" className={fieldClass}>
            <SelectValue placeholder="Select inquiry type" />
          </SelectTrigger>
          <SelectContent>
            {INQUIRY_TYPES.map((t) => (
              <SelectItem key={t.value} value={t.value} data-testid={`contact-inquiry-option-${t.value}`}>
                {t.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {errors.inquiry_type && <p role="alert" className={errorClass}>{errors.inquiry_type.message}</p>}
      </div>

      <div>
        <Label htmlFor="contact-message" className="text-slate-700">Message *</Label>
        <Textarea
          id="contact-message"
          data-testid="contact-message-input"
          rows={6}
          className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-400 focus-visible:ring-emerald-600"
          {...register("message")}
        />
        {errors.message && <p role="alert" className={errorClass}>{errors.message.message}</p>}
      </div>

      <div className="hidden" aria-hidden="true">
        <Label htmlFor="contact-website">Website</Label>
        <Input id="contact-website" tabIndex={-1} autoComplete="off" {...register("website")} />
      </div>

      <button
        type="submit"
        data-testid="contact-submit-button"
        disabled={submitting}
        className="inline-flex items-center gap-2 rounded-md bg-obsidian px-8 py-3.5 font-semibold text-white transition-[transform,background-color] duration-200 hover:-translate-y-0.5 hover:bg-pine hover:text-volt active:translate-y-0 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:translate-y-0"
      >
        {submitting ? "Sending…" : "Submit Inquiry"}
        <Send className="h-4 w-4" aria-hidden="true" />
      </button>
    </form>
  );
}
